import { useCallback, useContext, useEffect } from 'react';

import { AddFundsForm } from '~/components/modules/Forms/AddFundsForm';
import { AnalyticsEventNames } from '~/constants/AnalyticsEventMap';
import { DepositContext } from '~/context/Deposit/DepositContext';
import { LocalStorageHelper } from '~/helpers/StorageHelper';
import { useSavedPaymentMethod } from '~/hooks/payments/useSavedPaymentMethod';
import { useTracking } from '~/hooks/tracking/useTracking';
import { ModalMultiStepProps } from '~/models/deposits/ModalMultiStepProps';

import { useHandlePortfolioDestinationChange } from './PortfolioDestination';

export const TransferFunds = ({ setStep }: ModalMultiStepProps) => {
    const { captureEvent } = useTracking();
    const {
        setDepositAmount,
        setDepositFrequency,
        setPaymentMethod,
        destinationPortfolioId,
        setDestinationPortfolio,
        destinationPortfolio
    } = useContext(DepositContext);

    const handlePortfolioDestinationChange = useHandlePortfolioDestinationChange(setDestinationPortfolio);
    const { data: savedPaymentMethods, isSuccess } = useSavedPaymentMethod(destinationPortfolioId);

    const onDepositInfoEntered = useCallback(
        async (data: any) => {
            captureEvent(AnalyticsEventNames.DepositAmountAdded, {
                amount: Number(data?.amount),
                frequency: data?.frequency
            });
            setDepositAmount(Math.abs(Number(data?.amount)));
            setDepositFrequency(data?.frequency);
            await handlePortfolioDestinationChange(data?.destination);

            const selectedMethod = savedPaymentMethods?.paymentMethods.find(
                (value: { id: string }) => value?.id === data?.source
            );

            setPaymentMethod(selectedMethod);
            captureEvent(AnalyticsEventNames.ClickExistingPayment, undefined);
            const isConfirmed = LocalStorageHelper.getItem('confirmed-acknowledgements');
            if (
                (!isConfirmed && destinationPortfolio === 'whiskey') ||
                (!isConfirmed && destinationPortfolio == 'wine')
            ) {
                setStep(4);
            } else {
                setStep(1);
            }
        },
        [destinationPortfolioId, savedPaymentMethods]
    );

    const onPaymentChanged = (updatedPayment: string) => {
        if (updatedPayment === 'new') {
            setStep(3);
        }
    };

    useEffect(() => {
        if (savedPaymentMethods?.paymentMethods.length === 0) {
            setStep(3);
        } else {
            setStep(0);
        }
    }, [isSuccess, savedPaymentMethods?.paymentMethods, setStep, destinationPortfolioId]);

    return (
        <AddFundsForm
            savedPaymentMethod={savedPaymentMethods}
            onPaymentChanged={onPaymentChanged}
            onDepositInfoEntered={onDepositInfoEntered}
            loadedPaymentOptions={isSuccess}
        />
    );
};
